import type { XeroConnectionStatus } from '@prisma/client';
import { z } from 'zod';

/*
Xero OAuth scopes for the hub-level connection.

Apps created on or after 2 March 2026 are granular-only: the broad
`accounting.transactions` scope does not exist for this app and requesting it
fails the authorize call. Invoice-shaped objects live under
`accounting.invoices`; payment-shaped objects under `accounting.payments`.

The scope ticks in the Xero developer portal Configuration tab must equal this
list exactly — a mismatch is rejected at the authorize step, not at runtime.

GET https://api.xero.com/connections is documented as requiring no scope. If a
live call ever returns an insufficient-scope 403, add 'app.connections' both
here and in the portal.
*/
export const XERO_CONNECTION_SCOPES = [
  'offline_access',
  'accounting.contacts',        // read + write
  'accounting.invoices',        // read + write; granular umbrella (invoices, credit notes, quotes, POs, repeating, items)
  'accounting.payments.read',
  'accounting.settings.read',   // chart of accounts + tax rates, needed to build valid AU invoice lines
  'accounting.attachments',     // read + write; evidence upload onto invoices and bills
] as const;

export type XeroConnectionScope = (typeof XERO_CONNECTION_SCOPES)[number];

export interface XeroConnectionSafe {
  id: number;
  status: XeroConnectionStatus;
  xeroTenantId: string | null;
  xeroTenantName: string | null;
  connectedByMemberId: number | null;
  connectedByMemberEmail: string | null;
  lastVerifiedAt: string | null;
  revokedReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface XeroTenantCandidateSafe {
  id: number;
  xeroTenantId: string;
  xeroTenantName: string;
  xeroTenantType: string;
  xeroConnectionId: string;
}

export const XERO_HUB_CONNECT_BROADCAST_CHANNEL = 'vivport:xero-hub-connect';

export interface XeroHubConnectBroadcastMessage {
  type: 'vivport:xero-hub-connect';
  status: 'success' | 'error';
  reason?: string;
}

export interface XeroHubConnectStartResult {
  authUrl: string;
}

export interface XeroConnectionStateResult {
  connection: XeroConnectionSafe | null;
  candidates: XeroTenantCandidateSafe[];
  webhookUrl: string | null;
}

export interface XeroConnectionDisconnectResult {
  disconnected: true;
}

export const xeroSelectTenantSchema = z.object({
  xeroTenantId: z.string().trim().min(1),
}).strict();

export type XeroSelectTenantInput = z.infer<typeof xeroSelectTenantSchema>;

const xeroConnectionCallbackSuccessQuerySchema = z.object({
  code: z.string().trim().min(1),
  state: z.string().trim().min(1),
  error: z.undefined().optional(),
  error_description: z.undefined().optional(),
});

const xeroConnectionCallbackErrorQuerySchema = z.object({
  error: z.string().trim().min(1),
  error_description: z.string().trim().min(1).optional(),
  state: z.string().trim().min(1).optional(),
  code: z.undefined().optional(),
});

export const xeroConnectionCallbackQuerySchema = z.union([
  xeroConnectionCallbackSuccessQuerySchema,
  xeroConnectionCallbackErrorQuerySchema,
]);

export type XeroConnectionCallbackQuery = z.infer<typeof xeroConnectionCallbackQuerySchema>;
